import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

/**
 * Merges a query string onto a link, without overriding params the link already defines explicitly.
 */
export function appendQueryParams(url: string, queryString: string): string {
  if (!url || !queryString) return url;

  const [path, existingQuery = ''] = url.split('?');
  const params = new URLSearchParams(existingQuery);
  const incoming = new URLSearchParams(queryString);

  incoming.forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });

  const merged = params.toString();
  return merged ? `${path}?${merged}` : path;
}

/** Extracts the Compax offering id from an existing aboshop purchase URL. */
export function extractOfferingId(link?: string | null): string | undefined {
  return link?.match(/\/(\d{6,})(?=\/|\?|#|$)/)?.[1];
}

/** Extracts the Compax marketing code that follows the offering id in an aboshop purchase URL. */
export function extractMarketingCode(link?: string | null): string | undefined {
  return link?.match(/\/\d{6,}\/([^/?#]+)/)?.[1];
}

/**
 * Builds a purchase link for subscription CTAs while the in-page checkout is enabled.
 * The existing shop URL remains the fallback for entries whose offering id cannot be resolved.
 */
export function getSubscriptionBuyLink({
  link,
  queryString,
  fallbackOfferingId,
  marketingCode,
  discount = false,
}: {
  link?: string | null;
  queryString: string;
  fallbackOfferingId?: string | null;
  /** CMS-declared marketing code; only used when the link carries none. */
  marketingCode?: string | null;
  discount?: boolean;
}): string {
  if (process.env.NEXT_PUBLIC_NEW_ABOSHOP === 'true') {
    const offeringId = extractOfferingId(link) ?? fallbackOfferingId;
    if (offeringId) {
      // Path segment, not a query param: Varnish strips non-whitelisted query strings.
      const code = extractMarketingCode(link) || marketingCode;
      const path = `/abo/checkout/${offeringId}${code ? `/${code}` : ''}`;
      return appendQueryParams(`${process.env.NEXT_PUBLIC_APP_URL || ''}${path}`, queryString);
    }
  }

  const base = link ?? '/';
  const withDiscount = discount ? `${base}${base.includes('?') ? '&' : '?'}disc=true` : base;
  return appendQueryParams(withDiscount, queryString);
}

/**
 * The current page's query string, e.g. ad-campaign params like utm_*, gclid, fbclid.
 *
 * Reads window.location directly (not router.query/asPath): for getStaticProps pages Next only
 * populates router.query with the matched route params, not arbitrary query params, and doesn't
 * reliably re-render consumers once it does catch up. Starts empty so SSR/first client render match
 * (no hydration mismatch), then fills in post-mount via a real state update.
 */
export function useCurrentQueryString(): string {
  const { asPath } = useRouter();
  const [queryString, setQueryString] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: asPath is the re-run trigger for client-side nav, window.location.search is the actual source
  useEffect(() => {
    setQueryString(window.location.search.slice(1));
  }, [asPath]);

  return queryString;
}

/**
 * Forwards the current page's query string onto an outbound action link (abo/e-paper purchase
 * CTAs), so ad-campaign attribution survives the cross-domain hop to aboshop.tv-media.at.
 */
export function useActionLink(url: string): string {
  const queryString = useCurrentQueryString();
  return appendQueryParams(url, queryString);
}
